import React from 'react';
import { ArrowLeft, ShoppingBag, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 10: DAWN - Minimalist, Clean, Modern (Inspired by Shopify's Dawn theme)
const Template10 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-white min-h-screen font-sans">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <button
                            onClick={() => navigate('/TemplateThemes')}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            <ArrowLeft size={16} /> Back
                        </button>

                        <h1 className="text-xl font-semibold tracking-tight">DAWN</h1>

                        <div className="flex items-center gap-4">
                            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <Search size={20} className="text-gray-700" />
                            </button>
                            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <Heart size={20} className="text-gray-700" />
                            </button>
                            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <ShoppingBag size={20} className="text-gray-700" />
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section - Large Image with Text Overlay */}
            <section className="relative h-[600px] bg-gray-100">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=600&fit=crop"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20"></div>
                </div>
                <div className="relative h-full flex items-center justify-center text-center px-4">
                    <div className="max-w-2xl">
                        <h2 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-tight">
                            New Collection
                        </h2>
                        <p className="text-xl text-white/90 mb-8 font-light">
                            Discover our latest arrivals
                        </p>
                        <button className="px-8 py-3 bg-white text-gray-900 text-sm font-medium hover:bg-gray-100 transition-colors">
                            Shop Now
                        </button>
                    </div>
                </div>
            </section>

            {/* Featured Categories - Clean Grid */}
            <section className="max-w-7xl mx-auto px-4 py-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { title: 'Women', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=800&fit=crop' },
                        { title: 'Men', image: 'https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=600&h=800&fit=crop' },
                        { title: 'Accessories', image: 'https://images.unsplash.com/photo-1492707892479-7bc8d5a4ee93?w=600&h=800&fit=crop' }
                    ].map((category, index) => (
                        <div key={index} className="group relative overflow-hidden bg-gray-100 aspect-[3/4] cursor-pointer">
                            <img
                                src={category.image}
                                alt={category.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"></div>
                            <div className="absolute bottom-8 left-8 text-white">
                                <h3 className="text-2xl font-light mb-2">{category.title}</h3>
                                <span className="text-sm underline underline-offset-4">Shop Collection</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Products Grid - Minimalist */}
            <section className="max-w-7xl mx-auto px-4 py-16 bg-gray-50">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-light tracking-tight text-gray-900 mb-4">Featured Products</h2>
                    <p className="text-gray-600">Handpicked favorites</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((id) => (
                        <div key={id} className="group cursor-pointer">
                            <div className="relative overflow-hidden bg-white aspect-square mb-3">
                                <img
                                    src={`https://images.unsplash.com/photo-${1523381210376 + id * 100000}-7cbb3fc41fea?w=500&h=500&fit=crop`}
                                    alt={`Product ${id}`}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <h3 className="text-sm font-medium text-gray-900">Product Name</h3>
                            <p className="text-sm text-gray-600 mt-1">$XX.00</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Newsletter */}
            <section className="bg-gray-50 py-16">
                <div className="max-w-2xl mx-auto text-center px-4">
                    <h3 className="text-2xl font-light text-gray-900 mb-4">Stay Updated</h3>
                    <p className="text-gray-600 mb-8">Subscribe to get special offers and updates</p>
                    <div className="flex gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Email address"
                            className="flex-1 px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                        />
                        <button className="px-6 py-3 bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-200 py-12">
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-4">Shop</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li><a href="#" className="hover:text-gray-900 transition-colors">New Arrivals</a></li>
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Best Sellers</a></li>
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Sale</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-4">About</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Our Story</a></li>
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Contact</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-4">Help</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Shipping</a></li>
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Returns</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-4">Follow Us</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Instagram</a></li>
                            <li><a href="#" className="hover:text-gray-900 transition-colors">Facebook</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 mt-8 pt-8 border-t border-gray-200 text-center text-sm text-gray-600">
                    <p>&copy; 2025 Dawn. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Template10;
